import { Inviter, Registerer, SessionState, UserAgent } from 'sip.js';

export class Softphone {
  constructor(config, callbacks = {}) {
    this.config = config;
    this.callbacks = callbacks;
    this.userAgent = null;
    this.registerer = null;
    this.session = null;
    this.incoming = null;
    this.remoteAudio = null;
    this.remoteStream = null;
    this.startedAt = null;
    this.isMuted = false;
    this.operation = null;
    this.cleanedSessions = new WeakSet();
    this.lifecycleGeneration = 0;
    this.onlineHandler = null;
    this.reconnectPromise = null;
  }

  emit(state, extra = {}) { this.callbacks.onState?.({ state, ...extra }); }

  requestReconnect(userAgent, generation) {
    if (this.userAgent !== userAgent || this.lifecycleGeneration !== generation || userAgent.isConnected()) return Promise.resolve();
    if (this.reconnectPromise) return this.reconnectPromise;
    const reconnectPromise = userAgent.reconnect()
      .catch((error) => {
        if (this.userAgent === userAgent && this.lifecycleGeneration === generation) this.emit('reconnecting', { error });
        throw error;
      })
      .finally(() => {
        if (this.reconnectPromise === reconnectPromise && this.lifecycleGeneration === generation) this.reconnectPromise = null;
      });
    this.reconnectPromise = reconnectPromise;
    return reconnectPromise;
  }

  async connect() {
    if (this.userAgent) return;
    const generation = ++this.lifecycleGeneration;
    const uri = UserAgent.makeURI(`sip:${this.config.username}@${this.config.domain}`);
    if (!uri) throw new Error('No se pudo construir la URI SIP');
    const userAgent = new UserAgent({
      uri,
      transportOptions: { server: this.config.wsServer },
      // A temporary transport failure must not permanently disconnect an
      // authenticated operator. SIP.js retries forever with a bounded delay.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 4,
      authorizationUsername: this.config.username,
      authorizationPassword: this.config.password,
      delegate: {
        onInvite: (invitation) => this.receive(invitation),
        onConnect: () => {
          if (this.userAgent !== userAgent || this.lifecycleGeneration !== generation) return;
          this.registerer?.register().then(() => this.emit('registered')).catch(() => this.emit('reconnecting'));
        },
        onDisconnect: () => {
          if (this.userAgent === userAgent && this.lifecycleGeneration === generation) this.emit('reconnecting');
        },
      },
      sessionDescriptionHandlerFactoryOptions: { peerConnectionConfiguration: { iceServers: this.config.iceServers || [] } }
    });
    this.userAgent = userAgent;
    this.onlineHandler = () => {
      if (this.userAgent !== userAgent || this.lifecycleGeneration !== generation || userAgent.isConnected()) return;
      this.requestReconnect(userAgent, generation).catch(() => {});
    };
    window.addEventListener('online', this.onlineHandler);
    this.emit('connecting');
    try {
      await userAgent.start();
    } catch (error) {
      if (this.userAgent === userAgent) this.userAgent = null;
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
      throw error;
    }
    if (generation !== this.lifecycleGeneration || this.userAgent !== userAgent) {
      await userAgent.stop().catch(() => {});
      return;
    }
    const registerer = new Registerer(userAgent);
    this.registerer = registerer;
    try {
      await registerer.register();
    } catch (error) {
      if (this.userAgent === userAgent) {
        this.userAgent = null;
        this.registerer = null;
      }
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
      await userAgent.stop().catch(() => {});
      throw error;
    }
    if (generation !== this.lifecycleGeneration || this.userAgent !== userAgent) {
      await registerer.unregister().catch(() => {});
      await userAgent.stop().catch(() => {});
      return;
    }
    this.emit('registered');
  }

  receive(invitation) {
    if (this.session || this.incoming || this.operation) {
      invitation.reject().catch(() => {});
      return;
    }
    this.incoming = invitation;
    this.emit('incoming', { invitation });
  }

  attach(session) {
    this.session = session;
    session.stateChange.addListener((state) => {
      if (state === SessionState.Established) {
        this.startedAt = Date.now();
        this.bindAudio(session);
        this.emit('established', { startedAt: this.startedAt });
      } else if (state === SessionState.Terminated) {
        this.cleanupSession(session);
      }
    });
  }

  cleanupAudio() {
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((track) => track.stop());
      this.remoteStream = null;
    }
    if (this.remoteAudio) {
      this.remoteAudio.pause?.();
      this.remoteAudio.srcObject = null;
    }
  }

  cleanupSession(session = this.session) {
    if (session && this.cleanedSessions.has(session)) return;
    if (session) this.cleanedSessions.add(session);
    if (!session || this.session === session) this.session = null;
    if (!session || this.incoming === session) this.incoming = null;
    this.operation = null;
    this.startedAt = null;
    this.isMuted = false;
    this.cleanupAudio();
    this.emit('idle');
  }

  bindAudio(session) {
    const peerConnection = session.sessionDescriptionHandler?.peerConnection;
    if (!peerConnection || session.__platabusAudioBound) return;
    session.__platabusAudioBound = true;
    peerConnection.addEventListener('track', (event) => {
      if (event.streams[0] && this.remoteAudio) {
        this.remoteStream = event.streams[0];
        this.remoteAudio.srcObject = event.streams[0];
        this.remoteAudio.play().catch(() => {});
      }
    });
    const streams = peerConnection.getReceivers().map((receiver) => receiver.track).filter(Boolean);
    if (streams.length && this.remoteAudio) {
      this.remoteStream = new MediaStream(streams);
      this.remoteAudio.srcObject = this.remoteStream;
      this.remoteAudio.play().catch(() => {});
    }
  }

  async call(extension) {
    if (!this.userAgent || this.session || this.operation) throw new Error('El softphone no está disponible');
    const target = UserAgent.makeURI(`sip:${extension}@${this.config.domain}`);
    if (!target) throw new Error('Extensión inválida');
    const inviter = new Inviter(this.userAgent, target);
    this.attach(inviter);
    this.emit('outgoing', { target: extension });
    try {
      await inviter.invite();
    } catch (error) {
      this.cleanupSession(inviter);
      this.emit('error', { error });
      throw error;
    }
  }

  async accept() {
    if (!this.incoming || this.operation) return;
    const invitation = this.incoming;
    this.operation = 'answering';
    this.emit('answering');
    this.attach(invitation);
    this.incoming = null;
    try {
      await invitation.accept();
    } catch (error) {
      this.cleanupSession(invitation);
      this.emit('error', { error });
      throw error;
    } finally {
      if (this.operation === 'answering') this.operation = null;
    }
  }

  async reject() {
    if (!this.incoming || this.operation) return;
    const invitation = this.incoming;
    this.operation = 'rejecting';
    this.incoming = null;
    this.emit('rejecting');
    try {
      await invitation.reject();
    } catch (error) {
      this.emit('error', { error });
      throw error;
    } finally {
      this.cleanupSession(invitation);
    }
  }

  async hangup() {
    if (!this.session || this.operation === 'ending') return;
    const session = this.session;
    this.operation = 'ending';
    this.emit('ending');
    try {
      if (session.state === SessionState.Established) await session.bye();
      else if (session instanceof Inviter) await session.cancel();
      else await session.reject();
    } catch (error) {
      this.emit('error', { error });
    } finally {
      this.cleanupSession(session);
    }
  }

  toggleMute() {
    const pc = this.session?.sessionDescriptionHandler?.peerConnection;
    if (!pc || this.session?.state !== SessionState.Established) return this.isMuted;
    const audioTracks = pc.getSenders().map((sender) => sender.track).filter((track) => track?.kind === 'audio');
    if (!audioTracks.length) return this.isMuted;
    this.isMuted = !this.isMuted;
    audioTracks.forEach((track) => { track.enabled = !this.isMuted; });
    // Mute is media state, not a SIP session lifecycle state.
    this.emit('established', { startedAt: this.startedAt, isMuted: this.isMuted });
    return this.isMuted;
  }

  setAudioElement(element) {
    this.remoteAudio = element;
    if (this.session) this.bindAudio(this.session);
  }

  async disconnect({ unregister = false } = {}) {
    this.lifecycleGeneration += 1;
    this.reconnectPromise = null;
    this.cleanupSession();
    if (unregister) {
      try { await this.registerer?.unregister(); } catch {}
    }
    try { await this.userAgent?.stop(); } catch {}
    if (this.onlineHandler) window.removeEventListener('online', this.onlineHandler);
    this.onlineHandler = null;
    this.userAgent = null;
    this.registerer = null;
    this.session = null;
    this.incoming = null;
    this.emit('offline');
  }

  async logout() {
    return this.disconnect({ unregister: true });
  }
}
